/**
 * Centralized API error handling for Homy frontend.
 * Use with React Query mutations and queries: always show API errors in a toast.
 * @see kb/frontend/api-integration.md
 */

/**
 * Extracts a user-facing message from an API error (axios-style or thrown).
 * Handles CoreErrorResponse (message), ValidationErrorResponse (message/errors),
 * HTTPValidationError (detail[].msg), and network errors.
 *
 * @param {unknown} error - Caught error (usually axios error with response.data)
 * @param {string} fallbackMessage - Message when no structured message is found
 * @returns {string} User-facing error message
 */
export function getApiErrorMessage(
  error,
  fallbackMessage = "An error occurred",
) {
  if (!error) return fallbackMessage;

  // Axios: no response = connection refused, CORS blocked, offline, etc.
  if (!error.response) {
    const isAxiosNetworkError =
      Boolean(error.isAxiosError) ||
      error.code === "ERR_NETWORK" ||
      error.code === "ECONNABORTED";

    const isFetchNetworkError =
      typeof error.message === "string" &&
      /^(network error|failed to fetch)$/i.test(error.message.trim());

    if (
      (isAxiosNetworkError || isFetchNetworkError) &&
      typeof error.message === "string" &&
      error.message.trim()
    ) {
      return error.message.trim();
    }
    return fallbackMessage;
  }

  let data = error.response?.data;
  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      if (parsed && typeof parsed === "object") {
        data = parsed;
      }
    } catch {
      // Non-JSON error body (e.g. HTML) — fall through
    }
  }
  if (data && typeof data === "object") {
    // ValidationErrorResponse: errors by field (prevent generic "Validation failed" message from shadowing)
    if (data.errors && typeof data.errors === "object") {
      const messages = Object.values(data.errors).filter(Boolean);
      if (messages.length > 0) {
        return messages.length === 1 ? String(messages[0]) : messages.join(". ");
      }
    }
    // HTTPValidationError (OpenAPI/FastAPI): detail array
    if (Array.isArray(data.detail) && data.detail.length > 0) {
      const messages = data.detail
        .map((d) => (d && typeof d.msg === "string" ? d.msg : null))
        .filter(Boolean);
      if (messages.length > 0) {
        return messages.length === 1 ? messages[0] : messages.join(". ");
      }
    }
    // Structured detail object (FastAPI HTTPException with dict payload)
    if (
      data.detail &&
      typeof data.detail === "object" &&
      !Array.isArray(data.detail) &&
      typeof data.detail.message === "string" &&
      data.detail.message.trim()
    ) {
      return data.detail.message.trim();
    }
    // Local dev: FastAPI may include exception detail on 500
    if (typeof data.detail === "string" && data.detail.trim()) {
      return data.detail.trim();
    }
    // CoreErrorResponse / ValidationErrorResponse
    if (typeof data.message === "string" && data.message.trim()) {
      return data.message.trim();
    }
  }

  // Network or generic Error
  if (
    error.message &&
    typeof error.message === "string" &&
    error.message.trim()
  ) {
    return error.message.trim();
  }

  return fallbackMessage;
}

/**
 * Extracts the machine-readable error `code` from an API error body, when the
 * backend provides one (e.g. duplicate-resource 409s). Returns null otherwise.
 *
 * @param {unknown} error - Caught error (usually axios error with response.data)
 * @returns {string|null}
 */
export function getApiErrorCode(error) {
  if (!error || typeof error !== "object" || !error.response) return null;
  let data = error.response.data;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return null;
    }
  }
  if (
    data &&
    typeof data === "object" &&
    data.detail &&
    typeof data.detail === "object" &&
    !Array.isArray(data.detail) &&
    typeof data.detail.code === "string"
  ) {
    return data.detail.code.trim() || null;
  }
  if (data && typeof data === "object" && typeof data.code === "string") {
    return data.code.trim() || null;
  }
  return null;
}

/**
 * Normalizes response body for logging (same parsing as getApiErrorMessage).
 * @param {unknown} data
 * @returns {unknown}
 */
function normalizeResponseData(data) {
  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      if (parsed && typeof parsed === "object") {
        return parsed;
      }
    } catch {
      return data;
    }
  }
  return data;
}

/**
 * Best-effort multiline string for clipboard (support, debugging).
 * Includes summary message, HTTP metadata when axios-like, response body, optional trace headers.
 *
 * @param {unknown} error - Caught error (usually axios error with response)
 * @param {string} fallbackMessage - Message when no structured message is found
 * @returns {string}
 */
export function getApiErrorDetailsForCopy(
  error,
  fallbackMessage = "An error occurred",
) {
  const lines = [];
  const summary = getApiErrorMessage(error, fallbackMessage);
  lines.push(`Message: ${summary}`);

  if (error == null || typeof error !== "object") {
    lines.push(`Raw: ${String(error)}`);
    return lines.join("\n");
  }

  const err = error;

  if (!err.response) {
    lines.push("(No HTTP response — network, timeout, or client error.)");
    if (typeof err.message === "string" && err.message.trim()) {
      lines.push(`Client: ${err.message.trim()}`);
    }
    return lines.join("\n");
  }

  const { status, statusText, data: rawData, headers } = err.response;
  const statusLine = [status, statusText].filter(Boolean).join(" ");
  if (statusLine) {
    lines.push(`HTTP: ${statusLine}`);
  }

  const cfg = err.config;
  if (cfg && (cfg.method || cfg.url)) {
    const method = cfg.method ? String(cfg.method).toUpperCase() : "GET";
    const url = cfg.url ?? "";
    const base = cfg.baseURL ?? "";
    lines.push(`Request: ${method} ${base}${url}`);
  }

  if (headers && typeof headers === "object") {
    const rid =
      headers["x-request-id"] ??
      headers["X-Request-Id"] ??
      headers["x-correlation-id"] ??
      headers["X-Correlation-Id"];
    if (typeof rid === "string" && rid.trim()) {
      lines.push(`Request-Id: ${rid.trim()}`);
    }
  }

  const data = normalizeResponseData(rawData);
  if (data !== undefined && data !== null) {
    lines.push("Response body:");
    if (typeof data === "string") {
      const max = 8000;
      lines.push(data.length > max ? `${data.slice(0, max)}…` : data);
    } else {
      try {
        lines.push(JSON.stringify(data, null, 2));
      } catch {
        lines.push(String(data));
      }
    }
  }

  return lines.join("\n");
}
